import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasRole } from '../services/auth';

const AppFooter = () => {
  const { user } = useAuth();

  const getHomePath = () => {
    if (hasRole(user, 'client')) return '/client/home';
    if (hasRole(user, 'host')) return '/host/dashboard';
    if (hasRole(user, 'admin')) return '/admin';
    return '/login';
  };

  return (
    <footer className="border-t border-emerald-100 bg-white/80 backdrop-blur-sm mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <Link to={getHomePath()} className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-600 to-green-700 rounded-xl flex items-center justify-center shadow-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" className="w-4 h-4">
                  <circle cx="8" cy="8" r="4" fill="none" stroke="white" strokeWidth="1.5"/>
                  <circle cx="16" cy="8" r="4" fill="none" stroke="white" strokeWidth="1.5"/>
                  <circle cx="12" cy="16" r="4" fill="none" stroke="white" strokeWidth="1.5"/>
                </svg>
              </div>
              <span className="text-lg font-light text-gray-900">
                Spirara<span className="text-emerald-700">.</span>
              </span>
            </Link>
            <p className="text-sm text-gray-600 font-light max-w-md">
              Your gateway to transformative travel experiences. Connect with hosts, discover unique trips, and create memories that last a lifetime.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {hasRole(user, 'client') && (
                <>
                  <li>
                    <Link to="/client/home" className="text-sm text-gray-600 hover:text-emerald-700 transition-colors">
                      Home
                    </Link>
                  </li>
                  <li>
                    <Link to="/client/trips" className="text-sm text-gray-600 hover:text-emerald-700 transition-colors">
                      Browse Trips
                    </Link>
                  </li>
                  <li>
                    <Link to="/client/bookings" className="text-sm text-gray-600 hover:text-emerald-700 transition-colors">
                      My Bookings
                    </Link>
                  </li>
                </>
              )}
              {hasRole(user, 'host') && (
                <>
                  <li>
                    <Link to="/host/dashboard" className="text-sm text-gray-600 hover:text-emerald-700 transition-colors">
                      Dashboard
                    </Link>
                  </li>
                  <li>
                    <Link to="/host/trips" className="text-sm text-gray-600 hover:text-emerald-700 transition-colors">
                      My Trips
                    </Link>
                  </li>
                  <li>
                    <Link to="/host/bookings" className="text-sm text-gray-600 hover:text-emerald-700 transition-colors">
                      Bookings
                    </Link>
                  </li>
                </>
              )}
              {hasRole(user, 'admin') && (
                <>
                  <li>
                    <Link to="/admin" className="text-sm text-gray-600 hover:text-emerald-700 transition-colors">
                      Dashboard
                    </Link>
                  </li>
                  <li>
                    <Link to="/admin/hosts" className="text-sm text-gray-600 hover:text-emerald-700 transition-colors">
                      Host Approvals
                    </Link>
                  </li>
                  <li>
                    <Link to="/admin/trips" className="text-sm text-gray-600 hover:text-emerald-700 transition-colors">
                      Trip Approvals
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Support</h3>
            <ul className="space-y-2">
              <li>
                <a href="mailto:support@spirara.travel" className="text-sm text-gray-600 hover:text-emerald-700 transition-colors">
                  Contact Us
                </a>
              </li>
              <li>
                <Link to="/help" className="text-sm text-gray-600 hover:text-emerald-700 transition-colors">
                  Help Center
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="text-sm text-gray-600 hover:text-emerald-700 transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-sm text-gray-600 hover:text-emerald-700 transition-colors">
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-emerald-100 mt-8 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} Spirara. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a 
              href="https://x.com/spirara_app" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-emerald-600 transition-colors"
            >
              <span className="sr-only">Twitter</span>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a 
              href="https://www.instagram.com/spirara_app" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-emerald-600 transition-colors"
            >
              <span className="sr-only">Instagram</span>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default AppFooter;

